/**
 * Stable department identifiers for Carmel Work.
 *
 * Every task, user, and role must reference one of these canonical IDs.
 * Display names are editable; IDs are not.
 */
export const DEPARTMENTS = {
  ACADEMICS: "academics",
  ADMINISTRATION: "administration",
  OPERATIONS: "operations",
  FINANCE: "finance",
} as const;

export type DepartmentId = (typeof DEPARTMENTS)[keyof typeof DEPARTMENTS];

export interface DepartmentDefinition {
  id: DepartmentId;
  name: string;
  /** Role keys that serve as department heads. Empty if supervised by Admin/Superadmin. */
  headRoleKeys: string[];
  /** Role keys that supervise this department from the global level. */
  supervisingRoleKeys: string[];
}

export const DEPARTMENT_DEFINITIONS: readonly DepartmentDefinition[] = [
  {
    id: DEPARTMENTS.ACADEMICS,
    name: "Academics",
    headRoleKeys: ["principal"],
    supervisingRoleKeys: [],
  },
  {
    id: DEPARTMENTS.ADMINISTRATION,
    name: "Administration",
    headRoleKeys: [],
    supervisingRoleKeys: ["superadmin", "admin"],
  },
  {
    id: DEPARTMENTS.OPERATIONS,
    name: "Operations",
    headRoleKeys: ["operations_manager"],
    supervisingRoleKeys: [],
  },
  {
    id: DEPARTMENTS.FINANCE,
    name: "Finance",
    headRoleKeys: [],
    supervisingRoleKeys: ["superadmin", "admin"],
  },
] as const;

/** Lookup a department definition by its ID. */
export function getDepartmentById(id: string): DepartmentDefinition | undefined {
  return DEPARTMENT_DEFINITIONS.find((d) => d.id === id);
}

/** Check whether a role key is a department head for the given department. */
export function isDepartmentHeadRole(departmentId: string, roleKey: string): boolean {
  const dept = getDepartmentById(departmentId);
  return dept?.headRoleKeys.includes(roleKey) ?? false;
}

/** Get all department IDs. */
export function getAllDepartmentIds(): DepartmentId[] {
  return DEPARTMENT_DEFINITIONS.map((d) => d.id);
}
